import { isStampFabricObject } from '@/image-designer/adapters/stampAreasAdapter';
import { stampParamsFromLinkData } from './stampAreaConfig';

export const STAMP_LAYER_PART = {
  BACKDROP: 'stampBackdrop',
  FRAME: 'stampFrame',
  LABEL: 'stampLabel',
};

/** 印花区域框填充：蓝白色 90% 不透明度 */
export const STAMP_LAYER_FRAME_FILL = 'rgba(239, 246, 255, 0.9)';
export const STAMP_LAYER_FRAME_FILL_WITH_EXAMPLE = 'rgba(239, 246, 255, 0.45)';
export const STAMP_LAYER_LABEL_COLOR = '#2563eb';
export const STAMP_LAYER_FRAME_STROKE = '#2563eb';
const STAMP_LABEL_FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", sans-serif';

/** 根据印花框尺寸计算居中标签字号（对象本地坐标，随框缩放与画布 zoom 同比变化） */
export function computeStampLabelFontSize(width, height, textLength = 1) {
  const minDim = Math.max(1, Math.min(Math.abs(width), Math.abs(height)));
  let fontSize = Math.max(10, Math.round(minDim * 0.2));
  const len = Math.max(1, textLength);
  if (len > 6) {
    fontSize = Math.max(10, Math.round(fontSize * (6 / len)));
  }
  return fontSize;
}

function getStampDisplayName(stampRoot) {
  return String(stampRoot?.linkData?.stampName || stampRoot?.name || 'ipelayer').trim() || 'ipelayer';
}

export { getStampDisplayName };

function drawStampLabel(ctx, frame, stampRoot) {
  const root = stampRoot?.type === 'group' ? stampRoot : (stampRoot || frame);
  if (root?.linkData?.exampleImageUrl) return;

  const name = getStampDisplayName(root);
  const fontSize = computeStampLabelFontSize(frame.width || 0, frame.height || 0, name.length);

  ctx.save();
  ctx.font = `600 ${fontSize}px ${STAMP_LABEL_FONT_FAMILY}`;
  ctx.fillStyle = STAMP_LAYER_LABEL_COLOR;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Fabric Rect 在 _render 中以 (0, 0) 为几何中心绘制，而非 originX/originY 角点
  ctx.fillText(name, 0, 0);
  ctx.restore();
}

function installStampLabelRenderer(stampRoot) {
  const frame = getStampFrame(stampRoot) || stampRoot;
  if (!frame || frame._ipeStampLabelRenderer) return;

  const originalRender = frame._render.bind(frame);
  frame._render = function renderStampFrame(ctx) {
    originalRender(ctx);
    drawStampLabel(ctx, this, stampRoot.type === 'group' ? stampRoot : this);
  };
  frame._ipeStampLabelRenderer = true;
  frame.objectCaching = false;
}

export function isStampAreaLayer(obj) {
  return isStampFabricObject(obj);
}

export function getStampFrame(stampRoot) {
  if (!stampRoot) return null;
  if (stampRoot.type === 'rect' && isStampFabricObject(stampRoot)) return stampRoot;
  if (stampRoot.type === 'group' && isStampFabricObject(stampRoot)) {
    return stampRoot.getObjects?.().find((c) => c.linkData?.part === STAMP_LAYER_PART.FRAME) || null;
  }
  return null;
}

export function getStampBackdrop(stampRoot) {
  if (stampRoot?.type !== 'group' || !isStampFabricObject(stampRoot)) return null;
  return stampRoot.getObjects?.().find((c) => c.linkData?.part === STAMP_LAYER_PART.BACKDROP) || null;
}

/** 从点击/选中目标向上找到印花根（兼容 group 内嵌套） */
export function getStampRootFromTarget(obj) {
  let cur = obj;
  while (cur) {
    if (isStampFabricObject(cur)) return cur;
    cur = cur.group || null;
  }
  return null;
}

/** 遍历画布整棵对象树收集印花根（含组合内） */
export function collectStampRoots(canvas) {
  if (!canvas?.forEachObject) return [];
  const roots = [];
  const seen = new Set();
  canvas.forEachObject((obj) => {
    const root = getStampRootFromTarget(obj);
    if (!root?.id || seen.has(root.id)) return;
    seen.add(root.id);
    roots.push(root);
  });
  return roots;
}

/** 图层面板高亮用的图层 id（印花子框 → 印花根） */
export function resolveLayerPanelSelectionId(obj) {
  if (!obj?.id || obj.id === 'workspace') return null;
  const stampRoot = getStampRootFromTarget(obj);
  return stampRoot?.id || obj.id;
}

export function getStampFrameBounds(stampRoot) {
  const frame = getStampFrame(stampRoot);
  if (!frame) return null;

  const localWidth = Math.max(1, (frame.width || 0) * (Math.abs(frame.scaleX) || 1));
  const localHeight = Math.max(1, (frame.height || 0) * (Math.abs(frame.scaleY) || 1));
  let center;
  if (typeof frame.getCenterPoint === 'function') {
    center = frame.getCenterPoint();
  } else if (typeof frame.getBoundingRect === 'function') {
    const rect = frame.getBoundingRect(true, true);
    center = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  } else {
    center = {
      x: (frame.left ?? 0) + localWidth / 2,
      y: (frame.top ?? 0) + localHeight / 2,
    };
  }

  return {
    centerX: center.x,
    centerY: center.y,
    left: center.x - localWidth / 2,
    top: center.y - localHeight / 2,
    width: localWidth,
    height: localHeight,
    angle: frame.angle || 0,
  };
}

export function getStampFrameSize(stampRoot) {
  const frame = getStampFrame(stampRoot);
  if (!frame) {
    return {
      width: Math.round((stampRoot?.width || 0) * (stampRoot?.scaleX || 1)),
      height: Math.round((stampRoot?.height || 0) * (stampRoot?.scaleY || 1)),
    };
  }
  const bounds = frame.getBoundingRect(true, true);
  return {
    width: Math.max(1, Math.round(bounds.width)),
    height: Math.max(1, Math.round(bounds.height)),
  };
}

export function isStampRootSelected(stampRoot, canvas) {
  if (!stampRoot || !canvas) return false;
  const activeRoot = getStampRootFromTarget(canvas.getActiveObject?.());
  return Boolean(activeRoot && activeRoot.id === stampRoot.id);
}

export function applyStampFrameVisual(stampRoot, canvas) {
  if (!stampRoot) return;
  const frame = getStampFrame(stampRoot);
  if (!frame) return;

  const params = stampParamsFromLinkData(stampRoot.linkData);
  const hasExample = Boolean(stampRoot.linkData?.exampleImageUrl);
  const isSelected = isStampRootSelected(stampRoot, canvas);

  if (hasExample) {
    frame.set({
      stroke: params.stroke ? params.strokeColor : 'transparent',
      strokeWidth: params.stroke ? params.strokeWidth : 0,
      fill: 'transparent',
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: isSelected,
      lockMovementX: false,
      lockMovementY: false,
      lockRotation: false,
    });
  } else {
    frame.set({
      stroke: params.stroke ? params.strokeColor : STAMP_LAYER_FRAME_STROKE,
      strokeWidth: params.stroke ? params.strokeWidth : 2,
      fill: STAMP_LAYER_FRAME_FILL,
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true,
      lockMovementX: false,
      lockMovementY: false,
      lockRotation: false,
    });
  }

  const backdrop = getStampBackdrop(stampRoot);
  if (backdrop) {
    backdrop.set({
      fill: 'transparent',
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
    });
  }

  installStampLabelRenderer(stampRoot);
  frame.set('dirty', true);

  if (stampRoot.type === 'group') {
    stampRoot.set({
      selectable: false,
      evented: true,
      subTargetCheck: true,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      hasControls: false,
      hasBorders: false,
    });
    stampRoot.set('dirty', true);
    stampRoot.setCoords();
    return;
  }

  if (isStampFabricObject(stampRoot)) {
    stampRoot.setCoords();
  }
}

export function setStampFrameSize(stampRoot, width, height) {
  const frame = getStampFrame(stampRoot);
  if (!frame) return;

  frame.set({
    width: Math.max(1, Math.round(width)),
    height: Math.max(1, Math.round(height)),
    scaleX: 1,
    scaleY: 1,
  });
  frame.setCoords();

  if (stampRoot.type === 'group') {
    stampRoot.addWithUpdate?.(frame);
    stampRoot.set('dirty', true);
    stampRoot.setCoords();
  }
}

export function resizeStampFrameKeepingCenter(stampRoot, width, height) {
  const frame = getStampFrame(stampRoot);
  if (!frame) return;

  const center = frame.getCenterPoint();
  setStampFrameSize(stampRoot, width, height);
  frame.setPositionByOrigin(center, 'center', 'center');
  frame.setCoords();

  if (stampRoot.type === 'group') {
    stampRoot.addWithUpdate?.(frame);
    stampRoot.set('dirty', true);
    stampRoot.setCoords();
  }
}

/** 将印花旋转角度应用到矩形框（整个印花区域随框旋转） */
/** 将画布框旋转角度写回 linkData.stampRotation */
export function syncStampRotationFromFrame(stampRoot) {
  const frame = getStampFrame(stampRoot) || stampRoot;
  if (!frame) return;

  const angle = Math.round((((Number(frame.angle) || 0) % 360) + 360) % 360);
  const owner = stampRoot.linkData ? stampRoot : frame;
  if (!owner.linkData) {
    owner.linkData = { role: 'stampArea' };
  }
  owner.linkData.stampRotation = angle;
}

export function applyStampRotationToFrame(stampRoot, stampRotation) {
  const frame = getStampFrame(stampRoot) || stampRoot;
  if (!frame) return;

  const angle = Number(stampRotation) || 0;
  const center = frame.getCenterPoint();
  frame.set({ angle });
  frame.setPositionByOrigin(center, 'center', 'center');
  frame.setCoords();

  if (stampRoot.type === 'group') {
    stampRoot.set('dirty', true);
    stampRoot.setCoords();
  } else if (isStampFabricObject(stampRoot)) {
    stampRoot.setCoords();
  }
}

export function createStampLayerGroup(fabric, {
  id,
  name,
  linkData,
  workspace,
  frameLeft,
  frameTop,
  frameWidth,
  frameHeight,
}) {
  const frame = new fabric.Rect({
    id,
    name,
    left: (workspace.left || 0) + frameLeft,
    top: (workspace.top || 0) + frameTop,
    width: frameWidth,
    height: frameHeight,
    originX: 'left',
    originY: 'top',
    fill: STAMP_LAYER_FRAME_FILL,
    stroke: '#2563eb',
    strokeWidth: 2,
    lockUniScaling: true,
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    linkData: {
      ...linkData,
      role: 'stampArea',
    },
  });

  applyStampFrameVisual(frame);
  frame.setCoords();
  return frame;
}

export function getStampFrameFromFabricJson(stampObj) {
  if (!stampObj) return null;
  if (stampObj.type === 'rect') return stampObj;
  if (stampObj.type === 'group' && Array.isArray(stampObj.objects)) {
    return stampObj.objects.find((o) => o.linkData?.part === STAMP_LAYER_PART.FRAME) || null;
  }
  return null;
}

export function buildStampLayerFabricObject(stamp, workspace, bounds) {
  const params = stamp;
  const exampleFields = {};
  if (stamp.exampleImageUrl) {
    Object.assign(exampleFields, {
      exampleImageUrl: stamp.exampleImageUrl,
      exampleImageId: stamp.exampleImageId,
      exampleImageName: stamp.exampleImageName,
      exampleSource: stamp.exampleSource,
    });
  }

  return {
    type: 'rect',
    id: stamp.id,
    left: (workspace.left || 0) + bounds.left,
    top: (workspace.top || 0) + bounds.top,
    width: bounds.width,
    height: bounds.height,
    angle: bounds.angle || 0,
    originX: 'left',
    originY: 'top',
    fill: STAMP_LAYER_FRAME_FILL,
    stroke: '#2563eb',
    strokeWidth: 2,
    lockUniScaling: params.aspectRatio !== 'manual',
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    name: stamp.name,
    linkData: {
      role: 'stampArea',
      stampName: stamp.name,
      ...params,
      ...exampleFields,
    },
  };
}

export function flattenLegacyStampGroupOnCanvas(canvas, fabricNs) {
  if (!canvas || !fabricNs) return;
  const legacyGroups = collectStampRoots(canvas).filter((obj) => obj.type === 'group');
  legacyGroups.forEach((stampRoot) => {
    const bounds = getStampFrameBounds(stampRoot);
    if (!bounds) return;
    const idx = canvas.getObjects().indexOf(stampRoot);
    const rect = new fabricNs.Rect({
      id: stampRoot.id,
      name: stampRoot.name,
      left: bounds.left,
      top: bounds.top,
      width: bounds.width,
      height: bounds.height,
      angle: bounds.angle || 0,
      originX: 'left',
      originY: 'top',
      fill: STAMP_LAYER_FRAME_FILL,
      stroke: '#2563eb',
      strokeWidth: 2,
      linkData: { ...stampRoot.linkData },
    });
    canvas.remove(stampRoot);
    if (idx >= 0) {
      canvas.insertAt(rect, idx);
    } else {
      canvas.add(rect);
    }
    applyStampFrameVisual(rect);
  });
}

export function refreshStampSelectionVisuals(canvas) {
  if (!canvas) return;
  collectStampRoots(canvas).forEach((stampRoot) => {
    applyStampFrameVisual(stampRoot, canvas);
  });
  canvas.requestRenderAll();
}

export function refreshAllStampLayerVisuals(canvas, fabricNs) {
  if (!canvas) return;
  if (fabricNs) {
    flattenLegacyStampGroupOnCanvas(canvas, fabricNs);
  }
  collectStampRoots(canvas).forEach((stampRoot) => {
    const params = stampParamsFromLinkData(stampRoot.linkData);
    applyStampRotationToFrame(stampRoot, params.stampRotation);
    applyStampFrameVisual(stampRoot, canvas);
  });
  canvas.requestRenderAll();
}

/**
 * 若当前多选里含印花，归一为只选该印花框，避免点印花却框选到下方两层。
 * @returns {boolean} 是否发生了归一化
 */
export function normalizeActiveStampSelection(canvas) {
  if (!canvas) return false;
  const active = canvas.getActiveObject?.();
  if (!active) return false;

  if (active.type === 'activeSelection') {
    const objs = active.getObjects?.() || [];
    const stampRoots = [];
    const seen = new Set();
    objs.forEach((obj) => {
      const root = getStampRootFromTarget(obj);
      if (root?.id && !seen.has(root.id)) {
        seen.add(root.id);
        stampRoots.push(root);
      }
    });
    if (stampRoots.length !== 1) return false;
    const frame = getStampFrame(stampRoots[0]) || stampRoots[0];
    canvas.discardActiveObject();
    canvas.setActiveObject(frame);
    applyStampFrameVisual(stampRoots[0], canvas);
    canvas.requestRenderAll();
    return true;
  }

  const root = getStampRootFromTarget(active);
  if (!root) return false;
  const frame = getStampFrame(root);
  if (frame && active !== frame && active !== root) {
    canvas.setActiveObject(frame);
    applyStampFrameVisual(root, canvas);
    canvas.requestRenderAll();
    return true;
  }
  return false;
}
